/**
 * Utility for reading and setting Calendly's anonymous ID cookie.
 * This is tracked alongside Segment's anonymous ID for future analytics.
 * Cookie is only set after OneTrust consent (e.g. performance group).
 * ID format is UUID v4 to match Cloudflare/marketing site and backend analytics.
 */

import { v4 as uuid } from 'uuid'

import { Cookies } from 'services/cookies'

const CAL_ANONYMOUS_ID_COOKIE = 'cal_anonymous_id'
const CAL_ANONYMOUS_ID_EXPIRES_DAYS = 365

/**
 * Gets the Calendly anonymous ID from the server-set cookie
 * @returns {string|null} The Calendly anonymous ID or null if not found
 */
export const getCalendlyAnonymousId = () => {
  try {
    const cookies = new Cookies()
    const id = cookies.getCookie(CAL_ANONYMOUS_ID_COOKIE)
    return id || null
  } catch (error) {
    return null
  }
}

/**
 * Ensures cal_anonymous_id cookie exists when consent is given.
 * Returns existing cookie value if present; otherwise creates one with 365-day expiry and returns it.
 * Only sets the cookie when consentAllowed is true.
 * @param {boolean} consentAllowed - Whether OneTrust performance consent has been given
 * @returns {string|null} The Calendly anonymous ID or null if not set (e.g. no consent)
 */
export const ensureCalendlyAnonymousId = (consentAllowed) => {
  try {
    const cookies = new Cookies()
    const existing = cookies.getCookie(CAL_ANONYMOUS_ID_COOKIE)
    if (existing) return existing
    if (!consentAllowed) return null
    const id = uuid()
    cookies.setCookie(CAL_ANONYMOUS_ID_COOKIE, id, { expires: CAL_ANONYMOUS_ID_EXPIRES_DAYS })
    return id
  } catch (error) {
    return null
  }
}
